
'use client';

import { useState, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useGameReviews } from '@/context/GameReviewContext';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface AddGameReviewDialogProps {
  children: ReactNode;
}

export function AddGameReviewDialog({ children }: AddGameReviewDialogProps) {
  const { createReview } = useGameReviews();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast({ title: "Name required", description: "Please enter the name of the game.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      await createReview(name);
      setName('');
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to create review:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const onOpenChange = (open: boolean) => {
    if (!open) {
        setName('');
    }
    setIsOpen(open);
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Game Review</DialogTitle>
          <DialogDescription>
            Enter the name of the new game you want to review.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
            <div className="space-y-2">
                <Label htmlFor="game-name">Game Name</Label>
                <Input 
                  id="game-name" 
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Cyberpunk 2077"
                />
            </div>
        </div>
        <DialogFooter>
            <DialogClose asChild><Button type="button" variant="secondary">Cancel</Button></DialogClose>
            <Button type="submit" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add
            </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
